import { AnimationEvent } from '@angular/animations';
import { FocusOrigin } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import {
  AfterContentChecked,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Optional,
  Output,
} from '@angular/core';
import {
  distinctUntilChanged,
  filter,
  map,
  Observable,
  Subject,
  take,
} from 'rxjs';

import { DrawerToggleResult } from './drawer-types';

@Directive({
  selector: '[luiDrawerBase]',
  standalone: true,
})
export class DrawerBaseDirective implements AfterContentChecked {
  /** Whether the drawer can be closed with the escape key or by clicking on the backdrop. */
  @Input()
  get disableClose(): boolean {
    return this._disableClose;
  }
  set disableClose(value: BooleanInput) {
    this._disableClose = coerceBooleanProperty(value);
  }
  private _disableClose = false;

  // Opened properties
  @Input()
  get opened(): boolean {
    return this._opened;
  }
  set opened(value: BooleanInput) {
    this.toggle(coerceBooleanProperty(value));
    // this._opened = coerceBooleanProperty(value);
  }
  protected _opened = false;

  /** How the sidenav was opened (keypress, mouse click etc.) */
  protected _openedVia!: FocusOrigin | null;

  // Animation
  /** Whether the drawer is initialized. Used for disabling the initial animation. */
  protected _enableAnimations = false;

  _animationState: 'open-instant' | 'open' | 'closed' = 'closed';

  /** Emits whenever the drawer has started animating. */
  readonly _animationStarted = new Subject<AnimationEvent>();

  /** Emits whenever the drawer is done animating. */
  readonly _animationEnd = new Subject<AnimationEvent>();

  // Streams and emitters

  /** Event emitted when the drawer open state is changed. */
  @Output() readonly openedChange: EventEmitter<boolean> =
    // Note this has to be async in order to avoid some issues with two-bindings.
    new EventEmitter<boolean>(/* isAsync */ true);

  /** Event emitted when the drawer has been opened. */
  @Output()
  readonly openedStream = this.openedChange.pipe(
    filter((o) => o),
    map(() => undefined)
  );

  /** Event emitted when the drawer has started opening. */
  @Output()
  readonly openedStart: Observable<void> = this._animationStarted.pipe(
    filter((e) => e.fromState !== e.toState && e.toState.indexOf('open') === 0),
    map(() => undefined)
  );

  /** Event emitted when the drawer has been closed. */
  @Output()
  readonly closedStream = this.openedChange.pipe(
    filter((o) => !o),
    map(() => undefined)
  );

  /** Event emitted when the drawer has started closing. */
  @Output()
  readonly closedStart: Observable<void> = this._animationStarted.pipe(
    filter((e) => e.fromState !== e.toState && e.toState === 'void'),
    map(() => undefined)
  );

  constructor(
    protected _elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(DOCUMENT) protected _doc: Document,
    protected _platform: Platform
  ) {
    // We need a Subject with distinctUntilChanged, because the `done` event
    // fires twice on some browsers. See https://github.com/angular/angular/issues/24084
    this._animationEnd
      .pipe(
        distinctUntilChanged((x, y) => {
          return x.fromState === y.fromState && x.toState === y.toState;
        })
      )
      .subscribe((event: AnimationEvent) => {
        const { fromState, toState } = event;

        if (
          (toState.indexOf('open') === 0 && fromState === 'closed') ||
          (toState === 'closed' && fromState.indexOf('open') === 0)
        ) {
          this.openedChange.emit(this._opened);
        }
      });
  }

  /**
   * Toggle this drawer.
   * @param isOpen Whether the drawer should be open.
   * @param openedVia Whether the drawer was opened by a key press, mouse click or programmatically.
   * Used for focus management after the sidenav is closed.
   */
  toggle(
    isOpen: boolean = !this.opened,
    openedVia?: FocusOrigin
  ): Promise<DrawerToggleResult> {
    // If the focus is currently inside the drawer content and we are closing the drawer,
    // restore the focus to the initially focused element (when the drawer opened).
    if (isOpen && openedVia) {
      this._openedVia = openedVia;
    }

    const result = this._setOpen(
      isOpen,
      /* restoreFocus */ !isOpen && this._isFocusWithinDrawer(),
      this._openedVia || 'program'
    );

    if (!isOpen) {
      this._openedVia = null;
    }

    return result;
  }

  /**
   * Toggles the opened state of the drawer.
   * @param isOpen Whether the drawer should open or close.
   * @param restoreFocus Whether focus should be restored on close.
   * @param focusOrigin Origin to use when restoring focus.
   */
  protected _setOpen(
    isOpen: boolean,
    restoreFocus: boolean,
    focusOrigin: Exclude<FocusOrigin, null>
  ): Promise<DrawerToggleResult> {
    this._opened = isOpen;

    if (isOpen) {
      this._animationState = this._enableAnimations ? 'open' : 'open-instant';
    } else {
      this._animationState = 'closed';
      if (restoreFocus) {
        this._restoreFocus(focusOrigin);
      }
    }

    // this._updateFocusTrapState();

    return new Promise<DrawerToggleResult>((resolve) => {
      this.openedChange
        .pipe(take(1))
        .subscribe((open) => resolve(open ? 'open' : 'close'));
    });
  }

  /**
   * Open the drawer.
   * @param openedVia Whether the drawer was opened by a key press, mouse click or programmatically.
   * Used for focus management after the sidenav is closed.
   */
  open(openedVia?: FocusOrigin): Promise<DrawerToggleResult> {
    return this.toggle(true, openedVia);
  }

  /** Close the drawer. */
  close(): Promise<DrawerToggleResult> {
    return this.toggle(false);
  }

  _closeViaBackdropClick(): Promise<DrawerToggleResult> {
    return this._setOpen(/* isOpen */ false, /* restoreFocus */ true, 'mouse');
  }

  ngAfterContentChecked(): void {
    if (this._platform.isBrowser) {
      this._enableAnimations = true;
    }
  }

  /** Whether focus is currently within the drawer. */
  protected _isFocusWithinDrawer(): boolean {
    const activeEl = this._doc.activeElement;
    return !!activeEl && this._elementRef.nativeElement.contains(activeEl);
  }

  /**
   * Restores focus to the element that was originally focused when the drawer opened.
   * If no element was focused at that time, the focus will be restored to the drawer.
   */
  protected _restoreFocus(focusOrigin: Exclude<FocusOrigin, null>) {
    // if (this.autoFocus === 'dialog') {
    //   return;
    // }
    // if (this._elementFocusedBeforeDrawerWasOpened) {
    //   this._focusMonitor.focusVia(
    //     this._elementFocusedBeforeDrawerWasOpened,
    //     focusOrigin
    //   );
    // } else {
    //   this._elementRef.nativeElement.blur();
    // }
    // this._elementFocusedBeforeDrawerWasOpened = null;
  }
}
